import React from 'react';
import { Loader2, X } from 'lucide-react';
import type { DslTag, TagExecState } from './types';
import styles from './DslTagBank.module.css';

const TAG_LABEL_MAX_CHARS = 60;

const truncateDsl = (dsl: string): string => {
  if (dsl.length <= TAG_LABEL_MAX_CHARS) return dsl;
  return `${dsl.slice(0, TAG_LABEL_MAX_CHARS - 1)}…`;
};

const TagStatusIndicator: React.FC<{ state?: TagExecState }> = ({ state }) => {
  if (!state) return null;
  if (state.status === 'loading') {
    return (
      <span
        className={`${styles.tagStatus} ${styles.tagStatusLoading}`}
        data-testid="dsl-agent-tag-status-loading"
        title="Executing…"
        aria-label="Executing"
      >
        <Loader2 size={10} className={styles.tagStatusSpin} aria-hidden="true" />
      </span>
    );
  }
  if (state.status === 'ok') {
    return (
      <span
        className={`${styles.tagStatus} ${styles.tagStatusOk}`}
        data-testid="dsl-agent-tag-status-ok"
        title="Executed successfully"
        aria-label="Executed successfully"
      />
    );
  }
  if (state.status === 'error') {
    return (
      <span
        className={`${styles.tagStatus} ${styles.tagStatusError}`}
        data-testid="dsl-agent-tag-status-error"
        title={state.error ?? 'Execution failed'}
        aria-label={`Execution failed: ${state.error ?? 'Unknown error'}`}
      />
    );
  }
  return null;
};

export interface DslTagBankProps {
  tags: DslTag[];
  onRemoveTag: (id: string) => void;
  onClearAll: () => void;
  maxTags?: number;
  execStateById?: Record<string, TagExecState>;
}

export const DslTagBank: React.FC<DslTagBankProps> = ({
  tags,
  onRemoveTag,
  onClearAll,
  maxTags,
  execStateById,
}) => {
  const count = tags.length;
  const countLabel = typeof maxTags === 'number'
    ? `${count} of ${maxTags}`
    : `${count} tag${count === 1 ? '' : 's'}`;

  return (
    <div className={styles.container} data-testid="dsl-agent-tag-bank">
      <div className={styles.headerRow}>
        <label className={styles.headerLabel}>DSL tags</label>
        <div className={styles.headerMeta}>
          <span data-testid="dsl-agent-tag-count">{countLabel}</span>
        </div>
        <button
          type="button"
          className={styles.clearAllButton}
          onClick={onClearAll}
          disabled={count === 0}
          aria-label="Clear all DSL tags"
          data-testid="dsl-agent-tag-clear-all"
        >
          Clear all
        </button>
      </div>

      {count === 0 ? (
        <p className={styles.emptyState} data-testid="dsl-agent-tag-empty">
          No DSL tags yet. Generate a DSL expression to add one.
        </p>
      ) : (
        <ul className={styles.tagsContainer} role="list">
          {tags.map((tag) => {
            const label = truncateDsl(tag.dsl);
            const execState = execStateById?.[tag.id];
            return (
              <li
                key={tag.id}
                role="listitem"
                className={styles.tag}
                data-testid="dsl-agent-tag-pill"
                data-tag-id={tag.id}
                title={tag.dsl}
              >
                <span
                  className={styles.tagSwatch}
                  style={{ background: tag.color }}
                  aria-hidden="true"
                />
                <span className={styles.tagText}>{label}</span>
                <TagStatusIndicator state={execState} />
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => onRemoveTag(tag.id)}
                  aria-label={`Remove ${label}`}
                  data-testid="dsl-agent-tag-remove"
                >
                  <X size={12} aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
